import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import momentTz from 'moment-timezone';
import moment from 'moment';
import { Constants } from 'src/app/models/constants';
@Injectable({
  providedIn: 'root'
})
export class DateTimeService {

  constructor(@Inject(LOCALE_ID) private locale: string, private datePipe: DatePipe) { }

  getCurrentDateTime(format: string): string {
    if (format) {
      return formatDate(Date.now(), format, this.locale);
    }
    else {
      return new Date().toLocaleString();
    }
  }

  formatDateFromUTCTolocalDate(dateString, format = 'yyyy-MM-dd HH:mm:ss') {
    if (!dateString) {
      return '';
    }

    let date: Date;
    if (dateString.toUpperCase().includes('Z')) {
      date = new Date(dateString);
    } else {
      date = new Date(dateString + 'Z');
    }

    if (isNaN(date.getTime())) {
      return '';
    }
    return this.datePipe.transform(date, format) || '';
  }

  getUserCurrentDateTime(format = 'yyyyMMddHHmmSSS') {
    return this.datePipe.transform(new Date(), format);
  }

  getTimeZone(date = new Date()) {
    const userTimezone = momentTz.tz.guess();
    const timezoneOffset = momentTz.tz(userTimezone).format('Z');
    const timezoneAbbr = momentTz.tz(userTimezone).format('z');
    if (timezoneOffset === '+08:00') {
      return 'MYT';
    }
    return timezoneAbbr;
  }

  getUserTimeOffset() {
    return moment().format('Z');
  }

  dateFormate(date: string, format?: string): string {
    if (date != null && date != undefined && date != "null" && date != "") {
      const handleDate = `${new DatePipe('en-US').transform(format === 'UTC' ? (parseInt(date, 10) + 3 * 60 * 1000).toString() : date, 'yyyy-MM-dd HH:mm:ss', format ?? format)}`;
      return !!handleDate && handleDate !== 'null' ? handleDate : '';
    } else {
      return '';
    }
  }

  formatToUtcDate = (date: string, format: string = 'YYYY-MM-DD HH:mm:ss:SSS', endOfDay: boolean = false): string => {
    return endOfDay ? moment(date).endOf('day').utc().format(format) : moment(date).utc().format(format);
  }

  fromDateAndTime(fromDt, format = 'YYYY-MM-DD HH:mm:ss:SSS') {
    return this.formatToUtcDate(fromDt, format);
  }

  toDateAndTime(toDate, format = 'YYYY-MM-DD HH:mm:ss:SSS') {
    if (moment(toDate).isSame(moment(), 'day')) {
      return moment().utc().format(format);
    }
    return this.formatToUtcDate(toDate, format, true);
  }

  toWarsawUtcBoundary(
    dateInput: string,
    isEndOfDay: boolean,
    inputFormat: string = 'YYYY/MM/DD',
    outputFormat: string = 'YYYY-MM-DD HH:mm:ss:SSS'
  ): string | null {
    if (!dateInput) {
      return null;
    }

    const normalizedDate = dateInput.replace(/-/g, '/');
    const warsawDate = momentTz.tz(normalizedDate, inputFormat, 'Europe/Warsaw');

    if (!warsawDate.isValid()) {
      return null;
    }

    const boundary = isEndOfDay ? warsawDate.endOf('day') : warsawDate.startOf('day');
    return boundary.utc().format(outputFormat);
  }

  parseFlexibleDateForDateColumnFilter(input: string, filterMatchMode: string, toIsoUtc: boolean = false): string {
    if (!input || typeof input !== 'string') {
      return input;
    }
    let userTzOffset = this.getUserTimeOffset() || '+00:00'; // Default to user's timezone offset if not provided
    let value = input.trim();
    value = value.replace(/[-:]+$/, '');
    // Accept PL inputs with T separator and optional milliseconds without changing existing mode logic.
    value = value.replace('T', ' ');
    value = value.replace(/\.(\d{1,3})(?=$|Z|[+-]\d{2}:?\d{2}$)/, '');
    value = value.replace(/(Z|[+-]\d{2}:?\d{2})$/, '');
    const dateTimeParts = value.split(/[-\s:]/).filter(Boolean);
    const normalizedParts = dateTimeParts.map((p, i) =>
      i === 0 ? p : p.padStart(2, '0')
    );
    const typedPartsCount = normalizedParts.length;
    const operator = filterMatchMode?.toLowerCase() === Constants.notEqualsTo_between.toLowerCase() ? ' not between' : ' between';

    // --- Special case for startsWith with "2025-03-2" or "2025-3-2" ---
    if (filterMatchMode === Constants.startWith_between && typedPartsCount >= 3 && /^\d$/.test(dateTimeParts[2])) {
      const [y, M, dPrefix] = [
        dateTimeParts[0],
        dateTimeParts[1].padStart(2, '0'),
        dateTimeParts[2]
      ];

      const startDay = parseInt(dPrefix + '0', 10);
      const endDay = parseInt(dPrefix + '9', 10);

      const daysInMonth = moment(`${y}-${M}`, 'YYYY-MM').daysInMonth();
      const cappedEndDay = Math.min(endDay, daysInMonth);

      const start = moment.parseZone(`${y}-${M}-${String(startDay).padStart(2, '0')} 00:00:00${userTzOffset}`, 'YYYY-MM-DD HH:mm:ssZ',
        true).utc();

      const end = moment.parseZone(`${y}-${M}-${String(cappedEndDay).padStart(2, '0')} 23:59:59${userTzOffset}`, 'YYYY-MM-DD HH:mm:ssZ',
        true).utc();

      const fmt = toIsoUtc ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DD HH:mm:ss[Z]';
      return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
    }

    const [y, M, d, h, m, s] = [
      normalizedParts[0] || '2001',
      normalizedParts[1] || '01',
      normalizedParts[2] || '01',
      normalizedParts[3] || '00',
      normalizedParts[4] || '00',
      normalizedParts[5] || '00'
    ];

    let parsed = moment.parseZone(`${y}-${M}-${d} ${h}:${m}:${s}${userTzOffset}`, 'YYYY-MM-DD HH:mm:ssZ', true);

    if (!parsed.isValid()) {
      return input; // invalid → return as-is
    }

    const unit = typedPartsCount === 1 ? 'year' :
                typedPartsCount === 2 ? 'month' :
                typedPartsCount === 3 ? 'day' :
                typedPartsCount === 4 ? 'hour' :
                typedPartsCount === 5 ? 'minute' : 'second';

    const start = parsed.clone().startOf(unit).utc();
    const end = parsed.clone().endOf(unit).utc();

    const fmt = toIsoUtc ? 'YYYY-MM-DDTHH:mm:ss[Z]' : 'YYYY-MM-DD HH:mm:ss[Z]';

    switch (filterMatchMode) {
      case Constants.equalsTo_between:
        if (typedPartsCount < 6) {
          return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
        } else {
          return `= '${start.format(fmt)}'`;
        }
      case Constants.notEqualsTo_between:
        if (typedPartsCount < 6) {
          return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
        } else {
          return `!= '${start.format(fmt)}'`;
        }
      case Constants.startWith_between:
        return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
      default:
        return input;
    }
  }

 parseFlexibleDateForDateColumnFilterByTimezone(
    input: string,
    filterMatchMode: string,
    timezone: string,
    toIsoUtc: boolean = false
  ): string {
    if (!input || typeof input !== 'string' || !timezone) {
      return input;
    }

    let value = input.trim();
    value = value.replace(/[-:]+$/, '');
    if (filterMatchMode === Constants.startWith_between) {
      value = value.replace(/\.$/, '');
    }
    // Normalize UI-style values while preserving millisecond precision for exact matches.
    value = value.replace('T', ' ');
    value = value.replace(/(Z|[+-]\d{2}:\d{2})$/, '');
    value = value.replace(/\.(\d{1,3})$/, (_, ms) => `.${ms.padEnd(3, '0')}`);
    const hasMilliseconds = /\.\d{3}$/.test(value);
    const valueForParts = hasMilliseconds ? value.replace(/\.\d{3}$/, '') : value;
    const dateTimeParts = valueForParts.split(/[-\s:]/).filter(Boolean);
    const normalizedParts = dateTimeParts.map((p, i) => i === 0 ? p : p.padStart(2, '0'));
    const typedPartsCount = normalizedParts.length;
    const operator = filterMatchMode?.toLowerCase() === Constants.notEqualsTo_between.toLowerCase() ? ' not between' : ' between';

    if (filterMatchMode === Constants.startWith_between && typedPartsCount >= 3 && /^\d$/.test(dateTimeParts[2])) {
      const [y, M, dPrefix] = [
        dateTimeParts[0],
        dateTimeParts[1].padStart(2, '0'),
        dateTimeParts[2]
      ];

      const monthParsed = momentTz.tz(`${y}-${M}`, 'YYYY-MM', true, timezone);
      if (!monthParsed.isValid()) {
        return input;
      }

      const startDay = parseInt(dPrefix + '0', 10);
      const endDay = parseInt(dPrefix + '9', 10);
      const daysInMonth = monthParsed.daysInMonth();
      const cappedEndDay = Math.min(endDay, daysInMonth);

      const start = momentTz.tz(
        `${y}-${M}-${String(startDay).padStart(2, '0')} 00:00:00`,
        'YYYY-MM-DD HH:mm:ss',
        true,
        timezone
      ).utc().milliseconds(0);

      const end = momentTz.tz(
        `${y}-${M}-${String(cappedEndDay).padStart(2, '0')} 23:59:59`,
        'YYYY-MM-DD HH:mm:ss',
        true,
        timezone
      ).utc().milliseconds(999);

      const fmt = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';
      return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
    }

    const [y, M, d, h, m, s] = [
      normalizedParts[0] || '2001',
      normalizedParts[1] || '01',
      normalizedParts[2] || '01',
      normalizedParts[3] || '00',
      normalizedParts[4] || '00',
      normalizedParts[5] || '00'
    ];

    const parsedInput = hasMilliseconds ? `${y}-${M}-${d} ${h}:${m}:${s}.${value.split('.').pop()}` : `${y}-${M}-${d} ${h}:${m}:${s}`;
    const parsedFormat = hasMilliseconds ? 'YYYY-MM-DD HH:mm:ss.SSS' : 'YYYY-MM-DD HH:mm:ss';
    const parsed = momentTz.tz(parsedInput, parsedFormat, true, timezone);
    if (!parsed.isValid()) {
      return input;
    }

    const unit = hasMilliseconds ? 'millisecond' : typedPartsCount === 1 ? 'year' :
      typedPartsCount === 2 ? 'month' :
      typedPartsCount === 3 ? 'day' :
      typedPartsCount === 4 ? 'hour' :
      typedPartsCount === 5 ? 'minute' : 'second';

    const start = parsed.clone().startOf(unit).utc();
    const end = parsed.clone().endOf(unit).utc();
    const fmt = 'YYYY-MM-DDTHH:mm:ss.SSS[Z]';

    switch (filterMatchMode) {
      case Constants.equalsTo_between:
        if (hasMilliseconds) {
          return `= '${start.format(fmt)}'`;
        }
        return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
      case Constants.notEqualsTo_between:
        if (hasMilliseconds) {
          return `!= '${start.format(fmt)}'`;
        }
        return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
      case Constants.startWith_between:
        return `${operator} '${start.format(fmt)}' and '${end.format(fmt)}'`;
      default:
        return input;
    }
  }

}
